import React, { useContext, useState, useRef, useCallback, useEffect } from "react";
import { Typography } from "antd";
import { AppContext } from "../../../context/AppContext";
import CommonMessageTextArea from "../../../components/main/CommonMessageTextArea";
import useHttp from "../../../hooks/useHttp";
import { mapToMediaList } from "../../../pages/utils/commonUtils";
import type { ActionNodeData } from "../nodes/ActionNode";
import HCheckBoxGroup from "../../../voice/components/common/HCheckBoxGroup";
import { useWorkflowStore } from "../store";

const { Text } = Typography;

const noop = (): void => undefined;

export interface AutomatedResponseFormProps {
    data: ActionNodeData;
    update: (d: Partial<ActionNodeData>) => void;
}

/** Automated Response action form - uses CommonMessageTextArea for message input (templates, variables, emoji) */
export const AutomatedResponseForm: React.FC<AutomatedResponseFormProps> = ({ data, update }) => {
    const [appProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const applyOncePerContact = useWorkflowStore((s) => s.applyOncePerContact);
    const setApplyOncePerContact = useWorkflowStore((s) => s.setApplyOncePerContact);
    const [fileList, setFileList] = useState<unknown[]>([]);
    const [previewResponse, setPreviewResponse] = useState<{ components?: unknown } | null>(null);
    const [templateId, setTemplateId] = useState<string | undefined>(data.templateId);
    const [shortcutTemplateOpen, setShortcutTemplateOpen] = useState(false);
    const [iconTemplateOpen, setIconTemplateOpen] = useState(false);
    const [messageAreaLoading, setMessageAreaLoading] = useState(false);
    const messageTextAreaContentRef = useRef<HTMLDivElement>(null);

    const handleMessage = useCallback(
        (message: string, _event?: unknown, _source?: string) => {
            update({ message });
        },
        [update]
    );

    const toPersistableMedia = useCallback((items: unknown[]) => {
        return items.map((f) => {
            const item = f as Record<string, unknown>;
            return {
                url: item?.url as string | undefined,
                name: (item?.name as string) ?? "file",
                size: (item?.size as number) ?? -1,
                contentType: (item?.contentType as string) ?? (item?.type as string) ?? "dummy/dummy",
                thumbnailUrl: (item?.thumbnailUrl as string) ?? (item?.url as string),
            };
        });
    }, []);

    const handlePreviewResponse = useCallback(
        (response: {
            components?: unknown;
            data?: string;
            fieldNameAndValueMap?: Record<string, unknown>;
            mediaFileList?: unknown[];
            templateId?: string;
            templateName?: string;
            templateIntegId?: string;
        } | null) => {
            setPreviewResponse(response);
            if (response?.templateId != null) {
                const mediaList = Array.isArray(response.mediaFileList) ? response.mediaFileList : [];
                const persistableMedia = toPersistableMedia(
                    mediaList.map((f) => {
                        const item = f as Record<string, unknown>;
                        return {
                            url: item?.url,
                            name: item?.name ?? "file",
                            size: item?.size ?? -1,
                            contentType: (item?.contentType as string) ?? (item?.type as string) ?? "dummy/dummy",
                        };
                    })
                );
                update({
                    templateId: response.templateId,
                    templateName: response.templateName ?? undefined,
                    templateIntegId: response.templateIntegId ?? undefined,
                    message: response?.data ?? "",
                    hasMedia: mediaList.length > 0,
                    mediaFileList: persistableMedia,
                    components: response.components,
                    fieldNameAndValueMap: response.fieldNameAndValueMap,
                });
            }
        },
        [update, toPersistableMedia]
    );

    const handlePreviewRemove = useCallback(() => {
        setPreviewResponse(null);
        setTemplateId(undefined);
        update({
            message: "",
            templateId: undefined,
            templateName: undefined,
            templateIntegId: undefined,
            hasMedia: false,
            mediaFileList: [],
            components: undefined,
            fieldNameAndValueMap: undefined,
        });
    }, [update]);

    const handleMediaFileList = useCallback(
        (list: unknown[] | ((prev: unknown[]) => unknown[])) => {
            if (typeof list === "function") {
                setFileList((prev) => {
                    const nextList = list(prev);
                    const arr = Array.isArray(nextList) ? nextList : [];
                    update({ hasMedia: arr.length > 0, mediaFileList: toPersistableMedia(arr) });
                    return nextList;
                });
            } else {
                const arr = Array.isArray(list) ? list : [];
                setFileList(list);
                update({ hasMedia: arr.length > 0, mediaFileList: toPersistableMedia(arr) });
            }
        },
        [update, toPersistableMedia]
    );

    // Sync templateId from node data when it changes (e.g. load from server)
    useEffect(() => {
        if (data.templateId !== undefined) setTemplateId(data.templateId);
    }, [data.templateId]);

    // Restore fileList when switching nodes (data.mediaFileList persists across node changes)
    useEffect(() => {
        const persisted = data.mediaFileList;
        if (Array.isArray(persisted) && persisted.length > 0) {
            setFileList(mapToMediaList(persisted, false));
        } else {
            setFileList([]);
        }
    }, [data.mediaFileList, data.templateId]);

    // Restore preview when form opens with persisted templateId (e.g. after clicking another node and back)
    useEffect(() => {
        const tid = data.templateId;
        if (!tid || previewResponse?.components || !appProperties) return;
        const integId = data.templateIntegId ?? appProperties?.randomIntegId;
        if (!integId) return;
        setMessageAreaLoading(true);
        fetchData(
            `omessage/${integId}/template/${tid}/preview?associatedObjectId=&associatedObjectType=&phone=`,
            "GET",
            null,
            appProperties
        )
            .then((response) => {
                if (typeof response !== "string") return;
                const responseData = JSON.parse(response);
                if (responseData?.data?.data || responseData?.data?.mediaFileList || responseData?.data?.components) {
                    const preview = responseData?.data;
                    setPreviewResponse(preview);
                    const mediaList = Array.isArray(preview?.mediaFileList) ? preview.mediaFileList : [];
                    const persistableMedia = toPersistableMedia(
                        mediaList.map((f: unknown) => {
                            const item = f as Record<string, unknown>;
                            return {
                                url: item?.url,
                                name: item?.name ?? "file",
                                size: item?.size ?? -1,
                                contentType: (item?.contentType as string) ?? (item?.type as string) ?? "dummy/dummy",
                            };
                        })
                    );
                    update({
                        message: preview?.data ?? data.message ?? "",
                        hasMedia: mediaList.length > 0,
                        mediaFileList: persistableMedia,
                        components: preview?.components,
                        fieldNameAndValueMap: preview?.fieldNameAndValueMap,
                    });
                }
            })
            .catch(() => undefined)
            .finally(() => setMessageAreaLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- update is stable from store
    }, [data.templateId, data.templateIntegId, appProperties, fetchData, previewResponse?.components]);

    if (!appProperties) return null;

    const commonMessageProps = {
        message: data.message ?? "",
        handleMessage,
        handleMedia: noop,
        handleMediaFileList,
        handlePreviewResponse,
        appProperties,
        fileList,
        source: "automation" as const,
        integId: appProperties?.randomIntegId,
        moduleName: "",
        fetchAllTemplates: true,
        messageTextAreaContentRef,
        templateId,
        setTemplateId,
        previewResponse,
        shortcutTemplateOpen,
        setShortcutTemplateOpen,
        iconTemplateOpen,
        setIconTemplateOpen,
        chatView: false,
        textAreaMinRows: 4,
        fetchAndShowTemplatePreview: true,
        hiddenItems: [] as string[],
        messageAreaLoading,
        setMessageAreaLoading,
        handleManageSubscription: noop,
        toggleDrawer: noop,
        handlePreviewRemove,
        isContentPresent: !!(data.message || fileList.length > 0 || previewResponse?.components),
        filesPreviewPosition: "bottom",
    };

    return (
        <div className="px-1 py-3">
            <Text className="trigger-rule-config-panel__label hs-fw-500 hs-fs-12 hs-color-BlackRussian">Message</Text>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <CommonMessageTextArea {...(commonMessageProps as any)} />
            <div className="mt-3">
                <HCheckBoxGroup
                    options={[
                        { label: "Apply once per contact", value: "true" },
                    ]}
                    checkedValue={(applyOncePerContact ?? false) ? "true" : "false"}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setApplyOncePerContact((e.target as HTMLInputElement).value === "true")
                    }
                    wrapperClassName="hs-fs-12"
                />
                <Text className="hs-fs-12 hs-color-mediumDark d-block mt-1">
                    When enabled, this workflow runs only once per contact.
                </Text>
            </div>
        </div>
    );
};
